import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveActivityEvent, useLiveViewWebsocket } from '../../../api/resources/useLiveViewWebsocket';
import { ActivityRecording } from '../ActivityTile';
import { useTodaysActivities } from '../../../api/queries/live/todaysActivities';
import { useSnackbar } from '../../SnackbarProvider';

export function useLiveActivities(jobsiteId: string, token: string) {
  const { showSnackbar } = useSnackbar();

  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isCameraOnline, setIsCameraOnline] = useState(false);
  const [activities, setActivities] = useState<Map<string, ActivityRecording>>(new Map());
  const isNewActivity = useRef<boolean>(false);

  const { data, refetch: refetchPastActivities } = useTodaysActivities({ jobsiteId, enabled: false });

  const handleConnect = useCallback(() => {
    refetchPastActivities();
  }, [refetchPastActivities]);

  const handleEvent = useCallback((event: LiveActivityEvent) => {
    let captureStartAt = event.captureStartAt;

    if (!isToday(captureStartAt)) return;

    const type = event.type;
    if (type === 'license_plate_read' || type === 'datapoint_uploaded') return;

    const captureStartAtIsoStr = formatDatekey(captureStartAt);
    updateActivities(captureStartAtIsoStr, type);
  }, []);

  const updateActivities = (captureStartAt: string, eventType: string) => {
    setActivities((prev) => {
      const prevActivities = new Map(prev);

      let existing = prevActivities.get(captureStartAt);
      let base: ActivityRecording;
      if (existing) {
        base = existing;
      } else {
        isNewActivity.current = true;
        base = {
          // no record with this captureStartAt exists yet
          id: `live-${captureStartAt}`,
          captureStartAt: captureStartAt,
          imageStatus: 'not-started',
          materialStatus: 'not-started',
          edgeStatus: 'not-started',
          volumeStatus: 'not-started',
          soilStatus: 'not-started',
          licensePlateStatus: 'not-started',
          licensePlate: '',
        };
      }

      const updates: Partial<ActivityRecording> = {};

      switch (eventType) {
        case 'stitching_finished':
          updates.imageStatus = 'finished';
          updates.licensePlateStatus = 'finished';
          break;
        case 'classification_finished':
          updates.materialStatus = 'finished';
          break;
        case 'soil_classification_finished':
          updates.soilStatus = 'finished';
          break;
        case 'edge_estimation_finished':
          updates.edgeStatus = 'finished';
          break;
        case 'volume_estimation_finished':
          updates.volumeStatus = 'finished';
          break;
        case 'volume_estimation_skipped':
          updates.volumeStatus = 'error';
          break;
        default:
          console.warn('Unknown message type: ', eventType);
      }

      prevActivities.set(captureStartAt, { ...base, ...updates });

      return prevActivities;
    });
  };

  useEffect(() => {
    if (isNewActivity.current) {
      showSnackbar('activity.newActivity', 'info');
      isNewActivity.current = false;
    }
  }, [showSnackbar, activities.size]);

  useEffect(() => {
    setActivities((prev) => {
      const updated = new Map(prev);

      const pastActivities = data?.data.activities;
      pastActivities?.forEach((activity) => {
        if (!updated.has(activity.captureStartAt)) {
          updated.set(activity.captureStartAt, activity);
        } else {
          // If it exists, update the license plate if we have it now
          const existing = updated.get(activity.captureStartAt)!;
          if (!existing.licensePlate && activity.licensePlate) {
            updated.set(activity.captureStartAt, { ...existing, licensePlate: activity.licensePlate });
          }
        }
      });

      return updated;
    });
  }, [data?.data.activities]);

  useEffect(() => {
    setIsCameraOnline(connectionStatus === 'connected'); // temp camera state
  }, [connectionStatus]);

  useLiveViewWebsocket(jobsiteId, token, handleConnect, handleEvent, setConnectionStatus);

  return {
    isCameraOnline,
    activities,
    licensePlates: data?.data.licensePlates,
  };
}

function isToday(dateStr: string) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return false; // invalid date

  const now = new Date();

  return (
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  );
}

function formatDatekey(dateStr: string) {
  return new Date(dateStr).toISOString();
}
